import { FC, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { checkCityIataCodes } from 'entities/ticket/lib/getAgeForInfants'
import { DifficultRoute } from 'entities/ticket/models/find-ticket/models'

import {
  MAX_COUNT,
  TYPE_AGE,
  TicketClass,
  TicketClassList,
} from 'shared/constants'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { ButtonInput } from 'shared/ui/button-input'
import { Paper } from 'shared/ui/paper'
import { SelectOneOptionRegular } from 'shared/ui/select-one-option-regular'

import styles from './styles.module.scss'

interface OnChangeCount {
  name: string
  value: number | string
}

export interface CountAndClass {
  adult?: number
  child?: number
  infant?: number
  infants_with_place?: number
  cabin_class?: string
}

interface Props {
  data?: CountAndClass
  directions?: DifficultRoute[] | []
  onChangeCountHandler?: (data?: CountAndClass) => void
}

const initialState: CountAndClass = {
  child: 0,
  infant: 0,
  infants_with_place: 0,
  adult: 1,
  cabin_class: TicketClass?.e.key,
}

const LevelClassNCount: FC<Props> = ({
  data,
  directions,
  onChangeCountHandler,
}) => {
  const { t } = useTranslation()

  const [state, setState] = useState<CountAndClass>(() => {
    if (data) {
      return {
        adult: data?.adult,
        child: data?.child,
        infant: data?.infant,
        infants_with_place: data?.infants_with_place,
        cabin_class: data?.cabin_class,
      }
    } else {
      return initialState
    }
  })

  const onChange = (data: OnChangeCount) => {
    const { name, value } = data

    setState((data) => {
      if (!data) {
        return data
      }
      switch (name) {
        case TYPE_AGE.ADULTS:
        case TYPE_AGE.CHILDREN:
        case TYPE_AGE.INFANTS_WITHOUT_SEAT:
          // case TYPE_AGE.INFANTS_WITH_SEAT:
          data[name] = typeof value === 'string' ? parseInt(value) : value
          break
        case 'cabin_class':
          data.cabin_class = String(value)
          break
      }

      return { ...data }
    })
  }

  const { age } = checkCityIataCodes(directions)

  const INITIAL_COUNT_N_AGE = [
    {
      name: TYPE_AGE.ADULTS,
      title: t('adult'),
      subTitle: t('adult_description'),
    },
    {
      name: TYPE_AGE.CHILDREN,
      title: t('children'),
      subTitle: t('children_description', { age }),
    },
    // {
    //   name: TYPE_AGE.INFANTS_WITH_SEAT,
    //   title: t('infants'),
    //   subTitle: `${t('infants_description')}  ${t('with_seat')}`,
    // },
    {
      name: TYPE_AGE.INFANTS_WITHOUT_SEAT,
      title: t('infants'),
      subTitle: `${t('infants_description', { age })} ${t('without_seat')}`,
    },
  ]

  const onSubmit = () => {
    let keyObject: keyof typeof state

    for (keyObject in state) {
      if (state[keyObject] !== data?.[keyObject]) {
        return onChangeCountHandler?.(state)
      }
    }

    return onChangeCountHandler?.(state)
  }

  const getMaxCountByAge = ({
    state,
    name,
  }: {
    state: CountAndClass
    name: TYPE_AGE
  }) => {
    if (name == TYPE_AGE.INFANTS_WITHOUT_SEAT) {
      if ((state?.adult || 1) === state.infant!) {
        return state?.adult
      } else if ((state?.adult || 0) <= resultDifference) {
        return state.adult
      } else {
        const totalCountExcept =
          (state?.adult || 0) +
          (state?.child || 0) +
          (state?.infants_with_place || 0)

        return MAX_COUNT - totalCountExcept + resultDifference
      }
    }

    return resultDifference + state[name]!
  }

  const getMinCountByAge = ({
    state,
    name,
  }: {
    state: CountAndClass
    name: TYPE_AGE
  }) => {
    if (name == TYPE_AGE.ADULTS) {
      if (state[TYPE_AGE.INFANTS_WITHOUT_SEAT]! > 0) {
        return state[TYPE_AGE.INFANTS_WITHOUT_SEAT]!
      } else {
        return 1
      }
    }

    return 0
  }

  const ticketClassList = TicketClassList?.map((item) => ({
    ...item,
    name: t(item.name),
  }))

  const totalCountPassengers =
    (state?.adult || 0) +
    (state?.child || 0) +
    (state?.infant || 0) +
    (state?.infants_with_place || 0)
  const resultDifference = MAX_COUNT - totalCountPassengers
  return (
    <div className={styles.wrapperClass}>
      <div className={styles.titleWrapper}>
        <h3>{t('passengers')}</h3>
      </div>
      <div className={styles.wrapperCountPassenger}>
        <Paper padding={[16]}>
          <div className={styles.countPassenger}>
            {INITIAL_COUNT_N_AGE?.map((item, index) => (
              <div
                className={styles.item}
                key={item.name + index}
              >
                <ButtonInput
                  title={item.title}
                  minNumber={getMinCountByAge({
                    state,
                    name: item.name as TYPE_AGE,
                  })}
                  maxNumber={getMaxCountByAge({
                    state,
                    name: item.name as TYPE_AGE,
                  })}
                  subTitle={item.subTitle}
                  value={state?.[item.name]}
                  onChange={(num) => onChange({ name: item.name, value: num })}
                />
              </div>
            ))}
          </div>
        </Paper>
      </div>

      <SelectOneOptionRegular
        options={ticketClassList}
        value={state?.cabin_class}
        onChange={(value) => onChange({ name: 'cabin_class', value })}
      />

      <ButtonContinue onClick={onSubmit}>
        <span className={styles.text}>{t('continue')}</span>
      </ButtonContinue>
    </div>
  )
}

export default LevelClassNCount
